import { ChangeEvent, useContext, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';

import { IngestManagementConfig } from '@/__configs/config_types';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { fetchTitleChildrenAndVersionsV2 } from '@/redux/slices/titleV2Slice';
import { setRegisterAgainst, setSelectedTitles } from '@/redux/slices/ingestManagementSlice';
import { selectActiveTitle } from '@/redux/selectors/titleV2Selectors';
import { selectChildrenEntityTypes } from '@/redux/selectors/titleSelectors';
import {
  selectIngestManagementRegisterAgainst,
  selectIngestManagementSelectedRecentTitles,
  selectIngestManagementSelectedTitles
} from '@/redux/selectors/ingestManagementSelectors';
import { capitalizeNoun } from '@/_utils/utils';
import { SelectedTitlesV2 } from '@/components/IngestManagement/FileIngest/TitleAssociationV2/TitleAssociationV2';

type RegisterAgainstProps = {
  onRegisterEntityChange: (entity: TitleEntityV2, selectedTitles: SelectedTitlesV2) => void;
  onTitleChange: (title: TitleEntityV2, index: number) => void;
  handleClearForm: () => void;
};

const RegisterAgainst = ({ onRegisterEntityChange, onTitleChange, handleClearForm }: RegisterAgainstProps) => {
  const { disableRecentTitles, registerAgainstWhiteList } = useContext(ConfigContext).ingestManagementConfig as IngestManagementConfig;
  const dispatch = useAppDispatch();
  const selectedParentTitle = useAppSelector(selectActiveTitle);
  const childrenEntityTypes = useAppSelector(selectChildrenEntityTypes);
  const selectedTitles = useAppSelector(selectIngestManagementSelectedTitles);
  const selectedRecentTitles = useAppSelector(selectIngestManagementSelectedRecentTitles);
  const registerAgainst = useAppSelector(selectIngestManagementRegisterAgainst);

  const handleUpdateRegisterAgainst = (event: ChangeEvent<HTMLInputElement> | string) => {
    const selectedValue = typeof event === 'string' ? event : event.target.value;
    dispatch(setRegisterAgainst(selectedValue));

    if (selectedParentTitle && selectedParentTitle.entity_type === selectedValue) {
      onRegisterEntityChange(selectedParentTitle, [{ options: [selectedParentTitle], value: selectedParentTitle }]);
    } else if (selectedParentTitle && (disableRecentTitles || !selectedRecentTitles)) {
      dispatch(fetchTitleChildrenAndVersionsV2(selectedParentTitle.id))
        .unwrap()
        .then(({ children }) => {
          const newSelectedTitles = [...selectedTitles] as SelectedTitlesV2;
          if (newSelectedTitles.length > 0) {
            newSelectedTitles[0] = { options: children, value: null };
          } else {
            newSelectedTitles.push({ options: children, value: null });
          }
          dispatch(setSelectedTitles(newSelectedTitles));
        });
    }
  };

  const registerAgainstOptions = useMemo(() => {
    if (!selectedParentTitle || !childrenEntityTypes) return [];
    const options = [selectedParentTitle.entity_type].concat(Array.from(childrenEntityTypes)).filter((type) => type !== undefined);
    if (registerAgainstWhiteList && registerAgainstWhiteList.length > 0) {
      return options.filter((type) => registerAgainstWhiteList.includes(type)).map((type) => ({ key: type, label: capitalizeNoun(type) }));
    }
    return options.map((type) => ({ key: type, label: capitalizeNoun(type) }));
  }, [selectedParentTitle, childrenEntityTypes]);

  useEffect(
    function updateSelectedTitlesOnRegisterAgainstChanged() {
      handleClearForm();
      if (disableRecentTitles || !selectedRecentTitles?.length || !registerAgainst) {
        dispatch(setSelectedTitles([]));
      } else {
        const index = selectedRecentTitles.length - 1;
        onTitleChange(selectedRecentTitles[index].value as TitleEntityV2, index);
      }
    },
    [registerAgainst]
  );

  useEffect(
    function updateRegisterAgainstOnOptionsChanged() {
      if (disableRecentTitles) {
        if (registerAgainstOptions?.length === 1) {
          handleUpdateRegisterAgainst(registerAgainstOptions[0].key);
        }
        return;
      }

      const test = (selectedRecentTitles || []).at(-1)?.value?.entity_type ?? '';
      if (selectedRecentTitles?.length && registerAgainstOptions?.length && test.length > 0) {
        handleUpdateRegisterAgainst(test);
      } else if (registerAgainstOptions?.length === 1) {
        handleUpdateRegisterAgainst(registerAgainstOptions[0].key);
      }
    },
    [registerAgainstOptions]
  );

  const hasRegisterAgainstOptions = registerAgainstOptions && registerAgainstOptions.length > 1;
  if (!hasRegisterAgainstOptions) return null;

  return (
    <Wrapper>
      <Typography variant="h6" mb={2}>
        Register Against
      </Typography>
      <RadioGroup row value={registerAgainst} onChange={handleUpdateRegisterAgainst}>
        {registerAgainstOptions.map((option) => {
          const isSelected = registerAgainst === option.key;
          return (
            <StyledOption
              key={option.key}
              value={option.key}
              control={<StyledRadio selected={+isSelected} />}
              label={option.label}
              selected={+isSelected}
              labelPlacement="start"
            />
          );
        })}
      </RadioGroup>
    </Wrapper>
  );
};

export default RegisterAgainst;

const Wrapper = styled.div(({ theme }) => ({
  background: theme.palette.background.default,
  padding: `10px 20px`,
  borderRadius: 13
}));

const StyledOption = styled(FormControlLabel)<{ selected: number }>(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 120,
  borderRadius: 50,
  border: `1px solid ${theme.palette.primary.main}`,
  background: selected ? theme.palette.primary.main : 'transparent',
  color: selected ? theme.palette.common.black : theme.palette.text.primary,
  fontWeight: theme.typography.fontWeightBold,
  paddingLeft: 12,

  '&.MuiFormControlLabel-root': {
    marginLeft: 0,
    marginRight: 12,
    marginBottom: 12
  }
}));

const StyledRadio = styled(Radio)<{ selected: number }>(({ theme, selected }) => ({
  '&.MuiRadio-root': {
    color: selected ? theme.palette.common.black : theme.palette.common.white
  }
}));
